import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group';
import { Label } from '@components/ui/label';
import { MaterialRow } from './MaterialRow';
import { UploadPanel } from './UploadPanel';
import { SectionBadge } from './SectionBadge';
import { OptionCard } from './OptionCard';
import { useRovingRadioGroup } from '@generated/hooks/useRovingRadioGroup';
import type { Frequency, MaterialItem, MaterialSelection, Unit } from '@generated/types';
import { formatWeight } from '@generated/utils/units';
import { frequencyHelperText } from '@generated/utils/pricing';

const SKELETON_ROW_IDS = ['sk-row-1', 'sk-row-2', 'sk-row-3', 'sk-row-4', 'sk-row-5'];

const FREQUENCY_OPTIONS: { value: Frequency; label: string; description: string }[] = [
  { value: 'Monthly', label: 'Monthly', description: 'A shipment every month' },
  { value: 'Quarterly', label: 'Quarterly', description: 'Collect for three months, ship four times a year' },
  {
    value: 'Annual',
    label: 'One-Time / Annually',
    description: 'One-time shipment, backlog, or collect all year, ship once cycle',
  },
];

interface MaterialsCardProps {
  materials: MaterialItem[];
  loading: boolean;
  unit: Unit;
  onUnitChange: (u: Unit) => void;
  selections: Record<string, MaterialSelection>;
  onToggleMaterial: (id: string, checked: boolean) => void;
  onWeightChange: (id: string, lbs: number | null) => void;
  files: Record<string, File[]>;
  onFilesAdd: (id: string, files: File[]) => void;
  onFileRemove: (id: string, idx: number) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  showErrors: boolean;
  weightErrorIds: Set<string>;
  uploadErrorIds: Set<string>;
  frequency: Frequency | null;
  onFrequencyChange: (f: Frequency) => void;
}

export function MaterialsCard(props: MaterialsCardProps) {
  const {
    materials,
    loading,
    unit,
    onUnitChange,
    selections,
    onToggleMaterial,
    onWeightChange,
    files,
    onFilesAdd,
    onFileRemove,
    notes,
    onNotesChange,
    showErrors,
    weightErrorIds,
    uploadErrorIds,
    frequency,
    onFrequencyChange,
  } = props;

  const totalLbs = materials.reduce((sum, m) => {
    const sel = selections[m.id];
    if (sel?.checked && sel.weightLbs) return sum + sel.weightLbs;
    return sum;
  }, 0);

  const requiredMaterials = materials.filter((m) => m.requiresUpload && selections[m.id]?.checked);

  const freqHelper = frequencyHelperText(frequency);
  const frequencyValues = FREQUENCY_OPTIONS.map((o) => o.value);
  const { getItemProps: getFrequencyItemProps } = useRovingRadioGroup(frequencyValues, frequency, onFrequencyChange);

  return (
    <section className="mt-5 rounded-xl border border-border bg-card p-6 shadow-[var(--shadow-stack)] sm:p-8">
      <div className="flex items-start gap-3">
        <SectionBadge number={2} />
        <div>
          <h2 className="font-[family-name:var(--font-heading)] text-xl font-medium text-foreground">
            Your material and shipping frequency
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Check the material types you expect to send and the anticipated shipment cycle. If you have multiple
            categories there is no need to sort. We are able to process mixed plastics as they come.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <Label id="frequency-label" className="text-xs font-medium text-foreground">
          Shipping frequency
        </Label>
        <div
          role="radiogroup"
          aria-labelledby="frequency-label"
          className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3"
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <OptionCard
              key={opt.value}
              title={opt.label}
              description={opt.description}
              selected={frequency === opt.value}
              onSelect={() => onFrequencyChange(opt.value)}
              groupProps={getFrequencyItemProps(opt.value)}
            />
          ))}
        </div>
        {freqHelper ? <p className="mt-1.5 text-xs text-muted-foreground">{freqHelper}</p> : null}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-foreground">
        <span>Enter weights in</span>
        <ToggleGroup
          type="single"
          value={unit}
          onValueChange={(v) => v && onUnitChange(v as Unit)}
          className="inline-flex"
        >
          <ToggleGroupItem value="lbs" className="text-xs">
            Pounds
          </ToggleGroupItem>
          <ToggleGroupItem value="tons" className="text-xs">
            Tons
          </ToggleGroupItem>
        </ToggleGroup>
        <span>per shipment</span>
      </div>

      <div className="mt-4 space-y-2">
        {loading
          ? SKELETON_ROW_IDS.map((skeletonId) => (
              <div key={skeletonId} className="h-14 animate-pulse rounded-lg bg-secondary" />
            ))
          : materials.map((m) => (
              <MaterialRow
                key={m.id}
                material={m}
                unit={unit}
                checked={selections[m.id]?.checked ?? false}
                weightLbs={selections[m.id]?.weightLbs ?? null}
                onToggle={(checked) => onToggleMaterial(m.id, checked)}
                onWeightChange={(lbs) => onWeightChange(m.id, lbs)}
                invalid={showErrors && weightErrorIds.has(m.id)}
              />
            ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
        <span className="text-sm font-medium text-foreground">Estimated monthly total</span>
        <span className="tnum text-base font-medium text-foreground">
          {formatWeight(totalLbs, unit)} {unit}
        </span>
      </div>

      <UploadPanel
        materials={requiredMaterials}
        files={files}
        onFilesAdd={onFilesAdd}
        onFileRemove={onFileRemove}
        notes={notes}
        onNotesChange={onNotesChange}
        showErrors={showErrors}
        uploadErrorIds={uploadErrorIds}
      />
    </section>
  );
}
